import React, { useEffect, useState } from "react";
import ModalContainer from "../../../common/ModalContainer";
import Button from "../../../common/Button";
import TextareaInput from "../../../common/InputField/TextareaInput";
import useGetPendingWorkOrderList from "hooks/ProductionHooks/useGetPendingWorkOrderList";
import moment from "moment";
import VendorAvatar from "components/common/VendorAvatar";
import useGetUserProfile from "hooks/useGetUserProfile";
import { useNavigate, useParams } from "react-router-dom";
import { productionActions } from "redux/Ldb/actions";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useGetRejectedWorkOrderList from "hooks/ProductionHooks/useGetRejectedWorkOrderList";
import CustomTable from "components/common/CustomTable";
import TextInput from "components/common/InputField/TextInput";
import Dropdown from "components/common/Dropdown";
import { debounce, truncate } from "lodash";
import SelectInput from "components/common/InputField/SelectInput";
import { Tooltip } from "react-tooltip";
import { formatMoney, getCurrencyFromCurrencyCode } from "helpers";
import useGetWorkOrderTabDetails from "hooks/ProductionHooks/useGetWorkOrderTabDetails";
import Loader from "components/common/Loader";

type workOrderModalProps = {
    approved?: boolean;
    setApproved?: (data: boolean) => void;
    workOrderApproved?: boolean;
    setWorkOrderApproved?: (data: boolean) => void;
    workOrderStarted: boolean;
    setWorkOrderStarted: (data: boolean) => void;
    rejected: boolean;
    setRejected: (data: boolean) => void;
    workOrderRejected: boolean;
    setWorkOrderRejected: (data: boolean) => void;
    productionStopped: boolean;
    setProductionStopped: (data: boolean) => void;
    confirmProductionStopModal: boolean;
    setConfirmProductionStopModal: (data: boolean) => void;
    workOrderAutoGenerated?: boolean;
    setWorkOrderAutoGenerated?: (data: boolean) => void;
    showApproval?: number;
    setShowApproval?: any;
    productionStopModal: boolean;
    setProductionStopModal: (data: boolean) => void;
    rejectWorkOrder?: boolean;
    setRejectWorkOrder?: (data: boolean) => void;
    stoppageReason: string;
    setStoppageReason: (data: string) => void;
    workorderId: string;
    handleWorkOrderStatus;
    updatingWorkOrderStatus: boolean;
    productName: string;
    id: string;
    page?: string;
    bomList?: any;
    setBomList?: any;
    rawMaterialModal?: boolean;
    setRawMaterialModal?: (data: boolean) => void;
    plannedQuantity?: number;
    handleStartWorkOrder?: any;
    startingWorkOrder?: boolean;
};

const WorkOrderModal = ({
    approved,
    setApproved,
    workOrderApproved,
    setWorkOrderApproved,
    workOrderStarted,
    setWorkOrderStarted,
    rejected,
    setRejected,
    workOrderRejected,
    setWorkOrderRejected,
    productionStopped,
    setProductionStopped,
    confirmProductionStopModal,
    setConfirmProductionStopModal,
    workOrderAutoGenerated,
    setWorkOrderAutoGenerated,
    showApproval,
    setShowApproval,
    productionStopModal,
    setProductionStopModal,
    rejectWorkOrder,
    setRejectWorkOrder,
    stoppageReason,
    setStoppageReason,
    workorderId,
    handleWorkOrderStatus,
    updatingWorkOrderStatus,
    productName,
    page,
    bomList,
    setBomList,
    rawMaterialModal,
    setRawMaterialModal,
    plannedQuantity,
    handleStartWorkOrder,
    startingWorkOrder,
    id,
}: workOrderModalProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [profile] = useGetUserProfile();
    const [reason, setReason] = useState<string>("");
    const [workOrderId, setWorkOrderId] = useState<string>("");
    const [approveStatus, setApproveStatus] = useState<boolean>(false);
    const [workOrderAutoRejected, setWorkOrderAutoRejected] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [warehouse, setWarehouse] = useState<string>("");
    const [expandedRowIdx, setExpandedRowIdx] = useState<number>(-1);
    const [stockList, setStockList] = useState<{ [key: string]: any }[]>([]);
    const [notEnoughCount, setNotEnoughCount] = useState<number>(0);

    const { updatingWorkOrderApprovalStatus, updateWorkOrderApprovalStatusSuccess } =
        useAppSelector((state) => state?.production);

    const { data: pendingList, settings } = useGetPendingWorkOrderList();
    const { data: rejectedList, settings: rejectedSettings } = useGetRejectedWorkOrderList();
    const {
        data: tabDetails,
        isLoading: tabLoading,
        setQuery,
        query,
    } = useGetWorkOrderTabDetails(id);

    useEffect(() => {
        if (!productionStopModal) {
            setStoppageReason("");
        }
    }, [productionStopModal]);

    useEffect(() => {
        if (page === "workOrderList") {
            if (pendingList?.length >= 1 && settings?.approversId?.includes(profile?._id)) {
                setWorkOrderAutoGenerated(true);
            } else {
                setWorkOrderAutoGenerated(false);
            }
        }
    }, [pendingList, settings, page]);

    useEffect(() => {
        if (rejectedList?.length >= 1 && rejectedSettings?.approversId?.includes(profile?._id)) {
            setWorkOrderAutoRejected(true);
        }
    }, [pendingList, settings]);

    const handleUpdateWorkOrderApprovalStatus = (approve: boolean, workOrderId) => {
        const body = {
            approve: approve,
            ...(!approve && { reason }),
        };
        setWorkOrderId(workOrderId);
        dispatch(productionActions?.updateWorkOrderApprovalStatus(body, workOrderId));
    };

    useEffect(() => {
        if (Boolean(updateWorkOrderApprovalStatusSuccess)) {
            setRejectWorkOrder(false);
            setWorkOrderAutoGenerated(false);
            approveStatus ? setWorkOrderApproved(true) : setWorkOrderRejected(true);
            dispatch(productionActions.resetUpdateWorkOrderApprovalStatus());
        }
    }, [updateWorkOrderApprovalStatusSuccess]);

    useEffect(() => {
        !rejectWorkOrder && setReason("");
    }, [rejectWorkOrder]);

    const handleDebouncedSearch = debounce((e) => {
        setDebouncedSearch(e?.target?.value);
    }, 800);

    const handleClearWarehouse = () => {
        setWarehouse("");
    };
    const BatchesTableHeader = [
        { title: "", widthClass: "w-[48px] !font-rocGroteskMedium !border-slate-200" },

        { title: "Batch Id", widthClass: "w-[90px] !font-rocGroteskMedium !border-slate-200" },
        {
            title: "Raw material (SKU)",
            widthClass: "w-[356px] !font-rocGroteskMedium !border-slate-200",
        },
        {
            title: "Quantity available",
            widthClass: "w-[164px] !font-rocGroteskMedium !border-slate-200",
        },
        {
            title: "Input quantity to use",
            widthClass: "w-[156px] !font-rocGroteskMedium !border-slate-200",
        },
        {
            title: "Remaining quantity on inventory",
            widthClass: "w-[222px] !font-rocGroteskMedium !border-slate-200",
        },
    ];

    const BatchesTableBody = [1, 1, 1]?.map((bom, idx) => {
        return [
            {
                content: <div key={idx} className={`w-full  `}></div>,
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-200 text-sm text-gm-50 font-rocGroteskMedium px-2.5`}
                    >
                        SC-001
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`w-full  `}>
                        <div className="flex items-center gap-2 py-2 px-2.5">
                            {/* {bom?.inventory?.productImageDetails?.productAvatar ||
                            bom?.inventory?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        bom?.inventory?.productImageDetails?.productAvatar ||
                                        bom?.inventory?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={bom?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : ( */}
                            <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                <i className="ri-box-2-fill text-lg text-slate-500"></i>
                            </div>
                            {/* )} */}

                            <div className="text-sm">
                                <p className="text-g-75">
                                    {/* {truncate(bom?.inventory?.productName, { length: 20 })} */}
                                    Sugar cane
                                </p>
                                <p className="text-[#64748B]">
                                    {/* {bom?.inventory?.sku} */}
                                    SC123
                                </p>
                            </div>
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-200 text-sm text-gm-50 flex items-center  font-rocGroteskMedium `}
                    >
                        <p className="px-4">
                            {/* {bom?.quantity} {``}
                            {bom?.inventory?.productStockDetails?.unitOfMeasurement} */}
                            500 lbs
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-200 text-sm text-slate-500 font-rocGroteskMedium `}
                    >
                        <div className="flex items-center justify-start px-4">
                            <TextInput
                                // value={bom?.quantity}
                                name={"Quantity"}
                                type="number"
                                required={true}
                                // onChange={(e) => handleBOMData(e, idx)}
                                onWheel={(e: any) => e.target.blur()}
                                onKeyDown={(evt) =>
                                    ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                        evt.key
                                    ) && evt.preventDefault()
                                }
                                containerClassname=""
                                inputClassName="!w-auto"
                                inputContainerClassname={
                                    "  !w-full !flex justify-start !border-#0F846D"
                                }
                                // focusBorder="!border-[2px] !border-b-[2px] !border-[#000]"
                                rightIcon={
                                    <div className="">
                                        <i className="ri-information-line text-[#E4281D]"></i>
                                    </div>
                                }
                                leftIconClass="!relative left-0 cursor-default"
                                widthClass="!w-auto"
                            />
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`w-full border-slate-200 text-sm text-slate-500 flex items-center  font-rocGroteskMedium `}
                    >
                        <p className="px-4">
                            {/* {bom?.quantity} {``}
                            {bom?.inventory?.productStockDetails?.unitOfMeasurement} */}
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
        ];
    });

    const addQuantityToBatches = (data) => {
        const formatStockData = data?.map((item) => {
            const bomQuantity = item?.quantity * (plannedQuantity || 1);
            let qty = item?.scrap
                ? Number(calcTotalQuantity(bomQuantity, item?.scrap))
                : bomQuantity
                ? Number(bomQuantity)
                : 0;

            const updatedQty =
                item?.batches?.length > 0 &&
                item?.batches?.map((stock) => {
                    if (qty > 0 && stock?.stock?.productStockDetails?.stockLevel > 0) {
                        let stockQty =
                            qty - stock?.stock?.productStockDetails?.stockLevel < 0
                                ? qty
                                : stock?.stock?.productStockDetails?.stockLevel;
                        qty = qty - stockQty;
                        return {
                            ...stock,
                            quantity: stockQty,
                        };
                    } else {
                        const { quantity, ...rest } = stock;
                        return rest;
                    }
                });
            return {
                ...item,
                batches: updatedQty,
                quantity: item?.quantity,
                calcPlannedQuantity:
                    item?.quantity !== undefined ? item?.quantity * (plannedQuantity || 1) : "",
                batchesStockIsNotEnough: qty > 0 ? true : false,
            };
        });
        setBomList(formatStockData);
    };

    useEffect(() => {
        if (rawMaterialModal && plannedQuantity) {
            setQuery("bom");
            if (query === "bom") {
                addQuantityToBatches(tabDetails?.billOfMaterialIds);
            }
        }
    }, [rawMaterialModal, plannedQuantity, productName, tabDetails]);

    useEffect(() => {
        const count = bomList?.filter((item) => item?.batchesStockIsNotEnough);
        setNotEnoughCount(count?.length);
    }, [bomList]);

    const handleBOMData = (e, idx) => {
        const { name, value } = e.target;
        const copyArr = [...bomList];
        const copy = copyArr[idx];
        copy[name] = value;
        copyArr[idx] = copy;
        if (name === "quantity" || name === "scrap") {
            const quantityValue = name === "quantity" ? value : copyArr[idx]?.quantity;
            const scrapValue = name === "scrap" ? value : copyArr[idx]?.scrap;
            const bomQuantity = quantityValue * (plannedQuantity || 1);

            let qty = scrapValue
                ? Number(calcTotalQuantity(bomQuantity, scrapValue))
                : bomQuantity
                ? Number(bomQuantity)
                : 0;

            copyArr[idx].batches =
                copyArr[idx]?.batches?.length > 0 &&
                copyArr[idx]?.batches?.map((item) => {
                    if (qty > 0 && item?.stock?.productStockDetails?.stockLevel > 0) {
                        let stockQty =
                            qty - item?.stock?.productStockDetails?.stockLevel < 0
                                ? qty
                                : item?.stock?.productStockDetails?.stockLevel;
                        qty = qty - stockQty;

                        return {
                            ...item,
                            quantity: stockQty,
                        };
                    } else {
                        const { quantity, ...rest } = item;
                        return rest;
                    }
                });

            copyArr[idx] = {
                ...copyArr[idx],
                batchesStockIsNotEnough: qty > 0 ? true : false,
                calcPlannedQuantity: quantityValue * (plannedQuantity || 1),
            };

            if (expandedRowIdx === idx) {
                setStockList(copyArr[idx].batches?.length > 0 ? copyArr[idx].batches : []);
            }
        }
        setBomList(copyArr);
    };
    const handleBOMDelete = (idx) => {
        const copyBom = [...bomList];
        copyBom?.splice(idx, 1);
        setBomList(copyBom);
        setExpandedRowIdx(-1);
        setStockList([]);
    };

    const calcTotalQuantity = (quantity, scrap, unitPrice?: number) => {
        const quantityNum = quantity;
        const scrapNum = scrap;
        const percentage = (quantityNum * scrapNum) / 100;

        const totalQuantity = quantity ? Number(percentage) + Number(quantity) : 0;
        if (unitPrice !== undefined) {
            return Number(totalQuantity * unitPrice);
        }
        return totalQuantity ? Number(totalQuantity) : 0;
    };

    const BOMTableHeader = [
        { title: "Material", widthClass: "pl-4 w-[35%] !font-rocGroteskMedium border-slate-200" },
        { title: "Category", widthClass: "w-[15%] !font-rocGroteskMedium border-slate-200" },
        {
            title: "Curr. stock level",
            widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200",
        },
        { title: "Quantity", widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200" },
        { title: "Plan quantity", widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200" },
        {
            title: "Estimated scrap (%)",
            widthClass: "w-[12%] !font-rocGroteskMedium border-slate-200",
        },
        { title: "Actual quantity", widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200" },
        { title: "Unit price", widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200" },
        { title: "Total price", widthClass: "w-[20%] !font-rocGroteskMedium border-slate-200" },
        { title: "Action", widthClass: "w-[160px] !font-rocGroteskMedium border-slate-200" },

        { title: "", widthClass: "w-[40px] !font-rocGroteskMedium border-slate-200" },
    ];

    const BOMTableBody = bomList?.map((item, idx) => {
        const totalStockAvailable =
            item?.batches?.length > 0
                ? item?.batches?.reduce((sum, stock) => {
                      return sum + (stock?.quantity || 0);
                  }, 0)
                : 0;
        const calcQuantity = (plannedQuantity || 1) * item?.quantity;

        const totalNeeded = item?.scrap
            ? calcTotalQuantity(calcQuantity, item?.scrap)
            : item?.quantity
            ? calcQuantity
            : 0;

        const totalUnitPrice =
            item?.batches?.length > 0
                ? item?.batches?.reduce((sum, stock) => {
                      return (
                          sum +
                          (stock?.quantity
                              ? Number(
                                    stock?.stock?.productStockDetails?.unitCost?.amount ||
                                        item?.inventory?.unitCost?.amount
                                ) || 0
                              : 0)
                      );
                  }, 0)
                : 0;

        const totalPrice =
            item?.batches?.length > 0
                ? item?.batches?.reduce((sum, stock) => {
                      return (
                          sum +
                          (stock?.quantity
                              ? Number(
                                    stock?.stock?.productStockDetails?.unitCost?.amount ||
                                        item?.inventory?.unitCost?.amount
                                ) || 0
                              : 0) *
                              (stock?.quantity || 0)
                      );
                  }, 0)
                : 0;

        const deficit = Number(totalNeeded) - Number(totalStockAvailable);
        return [
            {
                content: (
                    <div key={idx} className={`w-full text-sm text-slate-900 `}>
                        <div>
                            <div id={`product-${idx}`} className="relative">
                                <div className="flex items-center gap-2 py-2 pr-2">
                                    {item?.inventory?.productImageDetails?.productAvatar ||
                                    item?.inventory?.productImageDetails?.productImages?.[0] ? (
                                        <VendorAvatar
                                            imageSrc={
                                                item?.inventory?.productImageDetails
                                                    ?.productAvatar ||
                                                item?.inventory?.productImageDetails
                                                    ?.productImages?.[0]
                                            }
                                            size={32}
                                            name={item?.productName}
                                            containerClassname="!rounded-md"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                            <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                        </div>
                                    )}

                                    <div className="text-sm">
                                        <p className="text-g-75">
                                            {truncate(item?.inventory?.productName, { length: 15 })}
                                        </p>
                                        {/* <p className="text-[#64748B]">{item?.inventory?.sku}</p> */}
                                        <p className={`text-[#64748B] text-[13px]`}>
                                            {`${
                                                item?.batches?.length > 0
                                                    ? item?.batches?.length
                                                    : 0
                                            } ${item?.batches?.length > 1 ? "batches" : "batch"}`}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Tooltip
                                anchorSelect={`#product-${idx}`}
                                place={"right"}
                                content={item?.productName}
                                style={{
                                    maxWidth: "250px",
                                    zIndex: 9999,
                                    background: "#000",
                                    borderRadius: "6px",
                                    border: "1px solid #E2E8F0",
                                    color: "#fff",
                                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                }}
                            />
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0 !border-slate-200",
                expandedRowAction: () => {
                    idx === expandedRowIdx ? setExpandedRowIdx(-1) : setExpandedRowIdx(idx);
                    idx !== expandedRowIdx &&
                        setStockList(item?.batches?.length > 0 ? item?.batches : []);
                },
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`w-full text-sm text-slate-900 `}>
                        <div>
                            <div id={`product-cateogry-${idx}`}>
                                <TextInput
                                    value={
                                        item?.inventoryCategory
                                            ? item?.inventoryCategory
                                            : item?.inventoryId && !item?.inventoryCategory
                                            ? "N/A"
                                            : ""
                                    }
                                    name="value"
                                    type={"text"}
                                    disabled={true}
                                    inputPlaceholder="Category"
                                    inputContainerClassname={
                                        "!border-none !rounded-none !bg-transparent"
                                    }
                                    inputClassName="w-full "
                                />
                            </div>

                            <Tooltip
                                anchorSelect={`#product-cateogry-${idx}`}
                                place={"right"}
                                content={item?.inventoryCategory || ""}
                                style={{
                                    maxWidth: "250px",
                                    zIndex: 9999,
                                    background: "#000",
                                    borderRadius: "6px",
                                    border: "1px solid #E2E8F0",
                                    color: "#fff",
                                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                }}
                            />
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div
                        className={`w-full text-[13px] text-slate-900 !font-rocGroteskRegular px-4`}
                    >
                        <p className="">
                            {item?.inventory?.productStockDetails?.stockLevel !== undefined
                                ? `${formatMoney()?.format(
                                      item?.inventory?.productStockDetails?.stockLevel
                                  )} ${
                                      item?.inventory?.productStockDetails?.unitOfMeasurement
                                          ? item?.inventory?.productStockDetails?.unitOfMeasurement
                                          : ""
                                  }`
                                : "N/A"}
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div
                        className={`w-full  text-sm text-slate-900 flex items-center justify-center`}
                    >
                        <div className="p-3">
                            <TextInput
                                value={item?.quantity || ""}
                                name={"quantity"}
                                type="number"
                                onChange={(e) => {
                                    handleBOMData(e, idx);
                                }}
                                inputPlaceholder="Quantity"
                                required={true}
                                containerClassname=""
                                inputContainerClassname={"!h-[36px] !border-g-75"}
                                inputClassName={"!border-g-75"}
                                onWheel={(e: any) => e.target.blur()}
                                onKeyDown={(evt) =>
                                    ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                        evt.key
                                    ) && evt.preventDefault()
                                }
                                rightIcon={
                                    item?.batchesStockIsNotEnough || item?.totalBatches === 0 ? (
                                        <i
                                            className="ri-information-line text-g-75 "
                                            id={`input-${idx}`}
                                        ></i>
                                    ) : (
                                        ""
                                    )
                                }
                            />

                            <div>
                                <Tooltip
                                    anchorSelect={`#input-${idx}`}
                                    place={"top"}
                                    content="Quantity needed is less than stock level available for this production run"
                                    style={{
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        background: "#000",
                                        borderRadius: "6px",
                                        border: "1px solid #E2E8F0",
                                        color: "#fff",
                                        boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`w-full  text-sm text-slate-500 px-2.5`}>
                        <p className="!font-rocGroteskRegular text-gm-50">
                            {item?.calcPlannedQuantity !== undefined
                                ? formatMoney()?.format(item?.calcPlannedQuantity)
                                : ""}
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`w-full  text-sm text-slate-900 `}>
                        <TextInput
                            value={item?.scrap || ""}
                            name="scrap"
                            type="number"
                            onChange={(e) => handleBOMData(e, idx)}
                            inputPlaceholder="Scrap"
                            containerClassname=""
                            inputClassName="w-full"
                            inputContainerClassname={"!border-0 !rounded-none "}
                            focusBorder="!border-[2px] !border-b-[2px] !border-[#000]"
                            onWheel={(e: any) => e.target.blur()}
                            onKeyDown={(evt) =>
                                ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(evt.key) &&
                                evt.preventDefault()
                            }
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`w-full  text-sm text-slate-500 px-2.5`}>
                        <p className="!font-rocGroteskRegular text-gm-50">
                            {item?.scrap
                                ? formatMoney()?.format(
                                      calcTotalQuantity(item?.calcPlannedQuantity, item?.scrap)
                                  )
                                : item?.quantity
                                ? formatMoney()?.format(item?.calcPlannedQuantity)
                                : ""}
                        </p>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },

            {
                content: (
                    <div className={`w-full text-sm text-slate-900 px-2.5 flex items-center`}>
                        <p className="text-slate-500">
                            {getCurrencyFromCurrencyCode(item?.unitCost?.currency)}
                        </p>

                        <div className="!font-rocGroteskRegular text-slate-900">
                            <p className="">
                                {totalUnitPrice !== undefined
                                    ? formatMoney()?.format(totalUnitPrice)
                                    : "0"}
                            </p>
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`w-full text-sm text-slate-900 px-2.5 flex items-center`}>
                        <p className="text-slate-500">
                            {getCurrencyFromCurrencyCode(item?.unitCost?.currency)}
                        </p>

                        <div className="!font-rocGroteskRegular text-slate-900">
                            <p className="">
                                {totalPrice !== undefined ? formatMoney()?.format(totalPrice) : "0"}
                            </p>
                        </div>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`pr-3 `}>
                        {item?.batchesStockIsNotEnough || item?.totalBatches === 0 ? (
                            <Button
                                btnText="Create PO"
                                btnClassname="!px-4 !bg-transparent !py-2 !text-g-60 !w-fit text-[13px] !border !border-slate-200 !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                                onClick={() =>
                                    navigate(
                                        `/dashboard/purchase/create-purchase-request?planId=${
                                            tabDetails?.planId
                                        }&inventoryId=${item?.inventoryId}&productName=${
                                            item?.inventory?.productName
                                        }&qty=${Math.ceil(deficit)}`,
                                        { state: { internalNavigation: true } }
                                    )
                                }
                                icon=<i className="ri-file-list-3-line"></i>
                            />
                        ) : (
                            ""
                        )}
                    </div>
                ),
                widthClass: "!border-slate-200 ",
            },
            {
                content: (
                    <div
                        className={`w-full text-sm text-slate-900 px-2.5 flex items-center`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <i
                            className={`ri-delete-bin-line text-slate-500 text-base ${
                                bomList?.length > 1 && "cursor-pointer"
                            } 
                        `}
                            onClick={() => {
                                bomList?.length > 1 && handleBOMDelete(idx);
                            }}
                        ></i>
                    </div>
                ),
                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200 !border-b",
            },
        ];
    });

    const tableHeader = [
        { title: "", widthClass: "w-[50%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[12%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[20%] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[160px] !font-rocGroteskMedium" },
        { title: "", widthClass: "w-[40px] !font-rocGroteskMedium" },
    ];

    const expandedTableBody = stockList?.map((stock, idx) => {
        return [
            {
                content: (
                    <div
                        className={`py-2 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                    >
                        <div className="flex items-center space-x-3">
                            {stock?.stock?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        stock?.stock?.productImageDetails?.productAvatar ||
                                        stock?.stock?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={stock?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-4 h-4 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-sm text-slate-500"></i>
                                </div>
                            )}
                            <span className="text-[#B18000]">
                                {truncate(bomList[expandedRowIdx]?.inventory?.productName, {
                                    length: 40,
                                })}
                                - {stock?.stock?.batchId}
                            </span>
                        </div>
                    </div>
                ),

                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200 !border-b",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className="">
                            {" "}
                            {stock?.stock?.productStockDetails?.stockLevel !== undefined
                                ? `${formatMoney()?.format(
                                      stock?.stock?.productStockDetails?.stockLevel
                                  )} ${
                                      bomList[expandedRowIdx]?.inventory?.productStockDetails
                                          ?.unitOfMeasurement
                                  }`
                                : ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        {stock?.quantity !== undefined
                            ? `${formatMoney()?.format(stock?.quantity)}`
                            : ""}
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b",
            },

            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className=""></p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className=""></p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className=""></p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className="">
                            {stock?.stock?.cost?.amount !== undefined
                                ? `${getCurrencyFromCurrencyCode(
                                      stock?.stock?.cost?.currency
                                  )} ${formatMoney()?.format(
                                      stock?.stock?.cost?.amount ||
                                          bomList[idx]?.inventory?.unitCost?.amount
                                  )}`
                                : ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}>
                        <p className="">
                            {stock?.stock?.cost?.amount !== undefined &&
                            stock?.quantity !== undefined
                                ? `${getCurrencyFromCurrencyCode(
                                      String(stock?.stock?.cost?.currency)
                                  )} ${formatMoney()?.format(
                                      Number(
                                          stock?.stock?.cost?.amount ||
                                              bomList[idx]?.inventory?.cost?.amount
                                      ) * Number(stock?.quantity)
                                  )}`
                                : ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}
                    ></div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 font-rocGroteskMedium text-sm text-[#B18000] `}
                    ></div>
                ),
                widthClass: "!border-slate-200 !border-b ",
            },
        ];
    });

    return (
        <div>
            <ModalContainer
                open={workOrderStarted}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    setWorkOrderStarted(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setWorkOrderStarted(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#00DB8F] flex items-center justify-center">
                                <i className="ri-checkbox-circle-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    Work Order Started!
                                </span>
                                <p className={`px-8  mt-1 text-base text-slate-500 text-center `}>
                                    The Work Order {productName && `for ${productName}`} has
                                    officially started. All systems are go, and the production
                                    process is now underway.
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Close"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setWorkOrderStarted(false);
                                }}
                            />
                            <Button
                                btnText="Track Production Progress"
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    if (!approved) {
                                        navigate(
                                            `/dashboard/production/work-order/${workorderId}?currTab=3`,
                                            { state: { internalNavigation: true } }
                                        );
                                        setWorkOrderStarted(false);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={approved || workOrderApproved}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    approved
                        ? setApproved(false)
                        : workOrderApproved && setWorkOrderApproved(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            approved
                                ? setApproved(false)
                                : workOrderApproved && setWorkOrderApproved(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#00DB8F] flex items-center justify-center">
                                <i className="ri-checkbox-circle-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    {approved
                                        ? `Work Order Approved! (1/3)`
                                        : workOrderApproved && "Work Order approved!"}
                                </span>
                                <p
                                    className={`${
                                        workOrderApproved || workOrderStarted ? "px-8" : ""
                                    } mt-1 text-base text-slate-500 text-center `}
                                >
                                    {approved
                                        ? ` You've successfully reviewed and approved the Work Order. Your part is done, and we've notified the next approver in line.`
                                        : workOrderApproved &&
                                          "Your Work Order has been approved and is ready for production. You can now track its status and receive updates throughout the process."}
                                </p>
                            </div>
                        </div>

                        {approved && (
                            <div className="px-6 pb-4">
                                <p className="text-sm text-gm-50 my-4">Approvals</p>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-2 pb-2 border-b border-b-[#F1F5F9]">
                                        <div className="flex items-center gap-2">
                                            <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                <i className="ri-user-line text-gm-50"></i>
                                            </div>
                                            <div className="">
                                                <p className="text-g-75 text-[13px]">
                                                    Kaimana Kalani{" "}
                                                    <span className="text-slate-500 text-[13px] ">
                                                        {" "}
                                                        - Production Manager{" "}
                                                    </span>
                                                </p>
                                                <p className="text-slate-500 text-xs ">
                                                    Sept 7, 2024 at 10:00 AM
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                Level 1
                                            </div>
                                            <div className="py-1 px-3 bg-[#D0F4ED] rounded text-xs text-g-75 ">
                                                Approved
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 pb-2 border-b border-b-[#F1F5F9]">
                                        <div className="flex items-center gap-2">
                                            <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                <i className="ri-user-line text-gm-50"></i>
                                            </div>
                                            <div className="">
                                                <p className="text-g-75 text-[13px]">
                                                    Kaimana Kalani{" "}
                                                    <span className="text-slate-500 text-[13px] ">
                                                        {" "}
                                                        - Production Manager{" "}
                                                    </span>
                                                </p>
                                                <p className="text-slate-500 text-xs ">
                                                    Sept 7, 2024 at 10:00 AM
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                Level 2
                                            </div>
                                            <div className="py-1 px-3 bg-[#F9DFB3] rounded text-xs text-g-75 ">
                                                Pending
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 pb-2">
                                        <div className="flex items-center gap-2">
                                            <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                <i className="ri-user-line text-gm-50"></i>
                                            </div>
                                            <div className="">
                                                <p className="text-g-75 text-[13px]">
                                                    Kaimana Kalani{" "}
                                                    <span className="text-slate-500 text-[13px] ">
                                                        {" "}
                                                        - Production Manager{" "}
                                                    </span>
                                                </p>
                                                <p className="text-slate-500 text-xs ">
                                                    Sept 7, 2024 at 10:00 AM
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                Level 3
                                            </div>
                                            <div className="py-1 px-3 bg-[#F9DFB3] rounded text-xs text-g-75 ">
                                                Pending
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Close"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    approved
                                        ? setApproved(false)
                                        : workOrderApproved && setWorkOrderApproved(false);
                                }}
                            />
                            <Button
                                btnText={
                                    approved
                                        ? "Track Work Orders"
                                        : workOrderApproved
                                        ? "Track Work Orders"
                                        : ""
                                }
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    if (!approved) {
                                        navigate(
                                            `/dashboard/production/work-order/${workOrderId}?currTab=3`,
                                            { state: { internalNavigation: true } }
                                        );
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={productionStopped}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    setProductionStopped(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setProductionStopped(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#DC2626] flex items-center justify-center">
                                <i className="ri-close-circle-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    Production Stopped
                                </span>
                                <p
                                    className={`${
                                        workOrderRejected || productionStopped ? "px-8" : ""
                                    } mt-1 text-sm text-slate-500 text-center `}
                                >
                                    Your production stoppage details have been successfully
                                    recorded. Relevant team members will be notified, and the
                                    production schedule will be updated accordingly.
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Close"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    productionStopped && setProductionStopped(false);
                                }}
                            />
                            <Button
                                btnText="Return to Dashboard"
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setProductionStopped(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={rejected || workOrderRejected}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    rejected
                        ? setRejected(false)
                        : workOrderRejected && setWorkOrderRejected(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            rejected
                                ? setRejected(false)
                                : workOrderRejected && setWorkOrderRejected(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#DC2626] flex items-center justify-center">
                                <i className="ri-close-circle-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    {rejected
                                        ? ` Work Order Rejected! (1/3)`
                                        : workOrderRejected && "Work Order rejected!"}
                                </span>
                                <p
                                    className={`${
                                        workOrderRejected || productionStopped ? "px-8" : ""
                                    } mt-1 text-sm text-slate-500 text-center `}
                                >
                                    {rejected
                                        ? `The Work Order has been rejected, and your feedback has been received. The requester will be notified for necessary revisions.
`
                                        : workOrderRejected &&
                                          "Your Work Order has been rejected and will not proceed to production. Please review the details and make necessary adjustments before resubmitting."}
                                </p>
                            </div>
                        </div>

                        {rejected && (
                            <div className="px-6 pb-4">
                                <p className="text-sm text-gm-50 my-4">Approvals</p>

                                <div className="space-y-2">
                                    <div className="pb-2 border-b border-b-[#F1F5F9]">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                    <i className="ri-user-line text-gm-50"></i>
                                                </div>
                                                <div className="">
                                                    <p className="text-g-75 text-[13px]">
                                                        Kaimana Kalani{" "}
                                                        <span className="text-slate-500 text-[13px] ">
                                                            {" "}
                                                            - Production Manager{" "}
                                                        </span>
                                                    </p>
                                                    <p className="text-slate-500 text-xs ">
                                                        Sept 7, 2024 at 10:00 AM
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                    Level 1
                                                </div>
                                                <div className="py-1 px-3 bg-[#FEE2E2] rounded text-xs text-g-75 ">
                                                    Rejected
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-[13px] text-slate-500 mt-2">
                                            I don’t like the way you structured the Work Order.
                                            Please recreated the Work Order again. Thank you
                                        </p>
                                    </div>
                                    <div className="pb-2 border-b border-b-[#F1F5F9]">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                    <i className="ri-user-line text-gm-50"></i>
                                                </div>
                                                <div className="">
                                                    <p className="text-g-75 text-[13px]">
                                                        Kaimana Kalani{" "}
                                                        <span className="text-slate-500 text-[13px] ">
                                                            {" "}
                                                            - Production Manager{" "}
                                                        </span>
                                                    </p>
                                                    <p className="text-slate-500 text-xs ">
                                                        Sept 7, 2024 at 10:00 AM
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                    Level 2
                                                </div>
                                                <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                    Cancelled
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pb-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                    <i className="ri-user-line text-gm-50"></i>
                                                </div>
                                                <div className="">
                                                    <p className="text-g-75 text-[13px]">
                                                        Kaimana Kalani{" "}
                                                        <span className="text-slate-500 text-[13px] ">
                                                            {" "}
                                                            - Production Manager{" "}
                                                        </span>
                                                    </p>
                                                    <p className="text-slate-500 text-xs ">
                                                        Sept 7, 2024 at 10:00 AM
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                    Level 3
                                                </div>
                                                <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                    Cancelled
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Close"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    rejected && setRejected(false);
                                }}
                            />
                            <Button
                                btnText={
                                    rejected
                                        ? "View Work Orders"
                                        : workOrderRejected
                                        ? "Edit details"
                                        : productionStopped
                                        ? "Return to Dashboard"
                                        : ""
                                }
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    rejected && setRejected(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={productionStopModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    setProductionStopModal(false);
                }}
            >
                <form
                    className="bg-white rounded-[8px] relative"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleWorkOrderStatus("cancelled", workorderId);
                    }}
                >
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setProductionStopModal(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-1">
                                <i className="ri-feedback-line text-gm-50 text-[40px]"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    Production Stoppage Details
                                </span>
                                <p className="mt-1 text-base text-slate-500 text-center ">
                                    Please provide the reasons for stopping this ongoing production{" "}
                                    {productName && `for ${productName}`}. Be as detailed as
                                    possible to ensure clarity.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 pb-6">
                            <TextareaInput
                                name="stoppageReason"
                                value={stoppageReason}
                                onChange={(e) => setStoppageReason(e?.target?.value)}
                                rows={5}
                                placeholder={"Reason for stoppage"}
                                required={true}
                            />
                        </div>
                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Cancel"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setProductionStopModal(false);
                                }}
                            />
                            <Button
                                btnText={"Stop Work Order"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-white !border border-n-20 !font-rocGroteskMedium !px-4 !bg-[#FF3D1D]"
                                type="submit"
                                isLoading={updatingWorkOrderStatus}
                            />
                        </div>
                    </div>
                </form>
            </ModalContainer>

            <ModalContainer
                open={confirmProductionStopModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    setConfirmProductionStopModal(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setConfirmProductionStopModal(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#FFB903] flex items-center justify-center">
                                <i className="ri-information-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-6 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px]">
                                    Confirm Production Stop
                                </span>
                                {/* Changes saved successfully! */}
                                <p className="mt-1 text-base text-slate-500 text-center ">
                                    Are you sure you want to stop the ongoing production{" "}
                                    {productName && `for ${productName}`}? Stopping the production
                                    will halt all processes and may impact the production timeline.
                                </p>
                                {/* Your production plan details have been updated and saved. The
                                changes are now reflected in your schedule. */}
                            </div>
                        </div>
                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Continue production"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setConfirmProductionStopModal(false);
                                }}
                            />
                            <Button
                                btnText={"Stop production"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setConfirmProductionStopModal(false);
                                    setProductionStopModal(true);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={rejectWorkOrder}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px]"
                showCloseIcon={false}
                closeModal={() => {
                    setRejectWorkOrder(false);
                }}
            >
                <form
                    className="bg-white rounded-[8px] relative"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdateWorkOrderApprovalStatus(false, workOrderId);
                        setApproveStatus(false);
                    }}
                >
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setRejectWorkOrder(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-1">
                                <i className="ri-feedback-line text-gm-50 text-[40px]"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    Reject Work Order?
                                </span>
                                <p className="mt-1 text-base text-slate-500 text-center ">
                                    Please provide the reasons for rejecting this Work Order{" "}
                                    {productName && `for ${productName}`}. Be as detailed as
                                    possible to ensure clarity.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 pb-6">
                            <TextareaInput
                                name={reason}
                                value={reason}
                                onChange={(e) => setReason(e?.target?.value)}
                                rows={5}
                                placeholder={"Comment"}
                                required={true}
                            />
                        </div>
                        <div className="border-t border-t-slate-200 py-4 px-6 flex items-center space-x-4">
                            <Button
                                btnText={"Cancel"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4"
                                onClick={() => {
                                    setRejectWorkOrder(false);
                                }}
                                disabled={updatingWorkOrderApprovalStatus}
                            />
                            <Button
                                btnText={"Reject Work Order"}
                                btnClassname="!py-3 cursor-pointer !text-[14px] !text-white !border border-n-20 !font-rocGroteskMedium !px-4 !bg-[#FF3D1D]"
                                type="submit"
                                disabled={updatingWorkOrderApprovalStatus}
                                isLoading={updatingWorkOrderApprovalStatus}
                            />
                        </div>
                    </div>
                </form>
            </ModalContainer>
            <ModalContainer
                open={workOrderAutoGenerated}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px] max-h-[80vh]"
                showCloseIcon={false}
                closeModal={() => {
                    setWorkOrderAutoGenerated(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative pb-20">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setWorkOrderAutoGenerated(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#00DB8F] flex items-center justify-center">
                                <i className="ri-checkbox-circle-fill text-[24px] text-white"></i>
                            </div>
                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    {pendingList?.length} Work Order(s) auto-generated
                                </span>
                                <p className={` mt-1 text-sm text-slate-500 text-center `}>
                                    Synth has successfully auto-generated Work Orders based on the
                                    latest production schedule. These orders include all necessary
                                    details, ensuring your production process stays on track.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 space-y-4">
                            {pendingList?.map((workOrder, idx) => (
                                <div className="border border-l-[4px] border-l-[#FFB902] border-y-slate-200 border-r-slate-200 rounded-md p-3">
                                    <div className="flex justify-between items-center">
                                        <div className="">
                                            <p className="text-slate-500 text-sm">
                                                WO-{workOrder?.taId}
                                            </p>
                                            <p className="text-[#000000] text-sm">
                                                {`${
                                                    workOrder?.quantity?.amount !== undefined
                                                        ? workOrder?.quantity?.amount
                                                        : ""
                                                } ${
                                                    workOrder?.quantity?.amount !== undefined &&
                                                    workOrder?.quantity?.unitOfMeasurement
                                                }`}{" "}
                                                of {workOrder?.inventory?.productName}.
                                            </p>
                                            <div className="flex gap-1">
                                                <p className="text-slate-500 text-sm">
                                                    {" "}
                                                    {workOrder?.startDate &&
                                                        moment(workOrder?.startDate)?.format(
                                                            "MMM DD, YYYY"
                                                        )}
                                                </p>
                                                {workOrder?.endDate && (
                                                    <i className="text-slate-500 text-sm ri-arrow-right-line"></i>
                                                )}
                                                <p className="text-slate-500 text-sm">
                                                    {workOrder?.endDate &&
                                                        moment(workOrder?.endDate)?.format(
                                                            "MMM DD, YYYY"
                                                        )}
                                                </p>

                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <div
                                                            className={`!ml-3 w-2 h-2 rounded-full shrink-0 flex items-center justify-center captialize
                                   ${
                                       workOrder?.status === "pending"
                                           ? "bg-[#6366F1]"
                                           : workOrder?.status === "completed"
                                           ? "bg-[#22C55E]"
                                           : workOrder?.status === "in-progress"
                                           ? "bg-[#3B82F6]"
                                           : workOrder?.status === "cancelled"
                                           ? "bg-[#EF4444]"
                                           : workOrder?.status === "on-hold"
                                           ? "bg-[#EAB308]"
                                           : workOrder?.status === "rescheduled"
                                           ? "bg-[#8B5CF6]"
                                           : workOrder?.status === "planned"
                                           ? "bg-[#14B8A6]"
                                           : workOrder?.status === "delayed"
                                           ? "bg-[#F97316]"
                                           : workOrder?.status === "partial-completed"
                                           ? "bg-[#0EA5E9]"
                                           : workOrder?.status && "bg-[#6B7280]"
                                   }
                                    `}
                                                        ></div>
                                                        <span className="capitalize text-sm">
                                                            {workOrder?.status === "in-progress"
                                                                ? "In progress"
                                                                : workOrder?.status === "on-hold"
                                                                ? "On hold"
                                                                : workOrder?.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {workOrder?.inventory?.productImageDetails?.productAvatar ||
                                        workOrder?.inventory?.productImageDetails
                                            ?.productImages?.[0] ? (
                                            <VendorAvatar
                                                imageSrc={
                                                    workOrder?.inventory?.productImageDetails
                                                        ?.productAvatar ||
                                                    workOrder?.inventory?.productImageDetails
                                                        ?.productImages?.[0]
                                                }
                                                size={32}
                                                name={workOrder?.inventory?.productName}
                                                containerClassname="!rounded-md"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                                <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                            </div>
                                        )}
                                    </div>

                                    <div className="">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-gm-50 my-4">
                                                Waiting for approval. (
                                                {workOrder?.approvers?.length}/
                                                {settings?.approversId?.length})
                                            </p>
                                            <i
                                                className={`${
                                                    showApproval === idx
                                                        ? "ri-arrow-down-s-line"
                                                        : "ri-arrow-up-s-line"
                                                }  text-lg text-gm-50 cursor-pointer`}
                                                onClick={() =>
                                                    showApproval !== idx
                                                        ? setShowApproval(idx)
                                                        : setShowApproval(-1)
                                                }
                                            ></i>
                                        </div>

                                        {showApproval === idx && (
                                            <div className="space-y-2 pb-4">
                                                {settings?.approvers?.map((approver, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center justify-between gap-2 pb-2 border-b border-b-[#F1F5F9]"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                                <i className="ri-user-line text-gm-50"></i>
                                                            </div>
                                                            <div className="">
                                                                <p className="text-g-75 text-[13px]">
                                                                    {approver?.fullName ||
                                                                        approver?.email}
                                                                </p>
                                                                <p className="text-slate-500 text-xs  capitalize ">
                                                                    {approver?.role}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                                Level {idx + 1}
                                                            </div>
                                                            <div
                                                                className={` ${
                                                                    workOrder?.approverIds?.includes(
                                                                        approver?._id
                                                                    )
                                                                        ? "bg-[#D0F4ED] text-slate-900"
                                                                        : "bg-[#F9DFB3] text-g-75"
                                                                } py-1 px-3  rounded text-xs  `}
                                                            >
                                                                {workOrder?.approverIds?.includes(
                                                                    approver?._id
                                                                )
                                                                    ? "Approved"
                                                                    : "Pending"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                <div className=" flex items-center space-x-2">
                                                    <Button
                                                        btnText="View details"
                                                        btnClassname="!py-2 cursor-pointer !text-[14px] !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                                        disabled={updatingWorkOrderApprovalStatus}
                                                        onClick={() => {
                                                            navigate(
                                                                `/dashboard/production/work-order/${
                                                                    workOrder?._id
                                                                }?currTab=${2}`,
                                                                {
                                                                    state: {
                                                                        internalNavigation: true,
                                                                    },
                                                                }
                                                            );
                                                            setWorkOrderAutoGenerated(false);
                                                        }}
                                                    />
                                                    <Button
                                                        btnText={"Approve"}
                                                        btnClassname="!py-2 cursor-pointer !text-[14px] !bg-n-20 !text-g-75 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                                        disabled={updatingWorkOrderApprovalStatus}
                                                        isLoading={updatingWorkOrderApprovalStatus}
                                                        onClick={() => {
                                                            setWorkOrderId(workOrder?._id);
                                                            handleUpdateWorkOrderApprovalStatus(
                                                                true,
                                                                workOrder?._id
                                                            );
                                                            setApproveStatus(true);
                                                        }}
                                                    />
                                                    <Button
                                                        btnText={"Reject"}
                                                        btnClassname="!py-2 cursor-pointer !text-[14px] !text-[#FF3D1D] !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit !bg-[#FEF2F2]"
                                                        disabled={updatingWorkOrderApprovalStatus}
                                                        onClick={() => {
                                                            setWorkOrderAutoGenerated(false);
                                                            setRejectWorkOrder(true);
                                                            setWorkOrderId(workOrder?._id);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={workOrderAutoRejected}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[600px] max-h-[80vh]"
                showCloseIcon={false}
                closeModal={() => {
                    setWorkOrderAutoRejected(false);
                }}
            >
                <div className="bg-white rounded-[8px] relative pb-20">
                    <i
                        className="ri-close-fill cursor-pointer text-gm-50 text-xl absolute  top-4 right-4"
                        onClick={() => {
                            setWorkOrderAutoRejected(false);
                        }}
                    ></i>
                    <div className="mt-4 ">
                        <div className="text-center flex justify-center flex-col items-center px-8 pt-10 mb-8">
                            <div className="mb-4 w-12 h-12 bottom-0 right-[-13px] rounded-full bg-[#DC2626] flex items-center justify-center">
                                <i className="ri-close-circle-fill text-[24px] text-white"></i>
                            </div>

                            <div className="px-4 font-rocGroteskMedium">
                                <span className="block mb-2 text-center text-[22px] text-gm-50">
                                    {rejectedList?.length} Work Order(s) Rejected!
                                </span>
                                <p className={` mt-1 text-sm text-slate-500 text-center `}>
                                    Synth has successfully auto-generated Work Orders based on the
                                    latest production schedule. These orders include all necessary
                                    details, ensuring your production process stays on track.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 space-y-4">
                            {rejectedList?.map((workOrder, idx) => (
                                <div className="border border-l-[4px] border-l-[#E4281D] border-y-slate-200 border-r-slate-200 rounded-md p-3">
                                    <div className="flex justify-between items-center">
                                        <div className="">
                                            <p className="text-slate-500 text-sm">
                                                WO-{workOrder?.taId}
                                            </p>
                                            <p className="text-[#000000] text-sm">
                                                {`${
                                                    workOrder?.quantity?.amount !== undefined
                                                        ? workOrder?.quantity?.amount
                                                        : ""
                                                } ${
                                                    workOrder?.quantity?.amount !== undefined &&
                                                    workOrder?.quantity?.unitOfMeasurement
                                                }`}{" "}
                                                of {workOrder?.inventory?.productName}.
                                            </p>
                                            <div className="flex gap-1">
                                                <p className="text-slate-500 text-sm">
                                                    {" "}
                                                    {workOrder?.startDate &&
                                                        moment(workOrder?.startDate)?.format(
                                                            "MMM DD, YYYY"
                                                        )}
                                                </p>
                                                {workOrder?.endDate && (
                                                    <i className="text-slate-500 text-sm ri-arrow-right-line"></i>
                                                )}
                                                <p className="text-slate-500 text-sm">
                                                    {workOrder?.endDate &&
                                                        moment(workOrder?.endDate)?.format(
                                                            "MMM DD, YYYY"
                                                        )}
                                                </p>

                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <div
                                                            className={`!ml-3 w-2 h-2 rounded-full shrink-0 flex items-center justify-center captialize
                                   ${
                                       workOrder?.status === "pending"
                                           ? "bg-[#6366F1]"
                                           : workOrder?.status === "completed"
                                           ? "bg-[#22C55E]"
                                           : workOrder?.status === "in-progress"
                                           ? "bg-[#3B82F6]"
                                           : workOrder?.status === "cancelled"
                                           ? "bg-[#EF4444]"
                                           : workOrder?.status === "on-hold"
                                           ? "bg-[#EAB308]"
                                           : workOrder?.status === "rescheduled"
                                           ? "bg-[#8B5CF6]"
                                           : workOrder?.status === "planned"
                                           ? "bg-[#14B8A6]"
                                           : workOrder?.status === "delayed"
                                           ? "bg-[#F97316]"
                                           : workOrder?.status === "partial-completed"
                                           ? "bg-[#0EA5E9]"
                                           : workOrder?.status && "bg-[#6B7280]"
                                   }
                                    `}
                                                        ></div>
                                                        <span className="capitalize text-sm">
                                                            {workOrder?.status === "in-progress"
                                                                ? "In progress"
                                                                : workOrder?.status === "on-hold"
                                                                ? "On hold"
                                                                : workOrder?.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {workOrder?.inventory?.productImageDetails?.productAvatar ||
                                        workOrder?.inventory?.productImageDetails
                                            ?.productImages?.[0] ? (
                                            <VendorAvatar
                                                imageSrc={
                                                    workOrder?.inventory?.productImageDetails
                                                        ?.productAvatar ||
                                                    workOrder?.inventory?.productImageDetails
                                                        ?.productImages?.[0]
                                                }
                                                size={32}
                                                name={workOrder?.inventory?.productName}
                                                containerClassname="!rounded-md"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                                <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                            </div>
                                        )}
                                    </div>

                                    <div className="">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-[#E4281D] my-4">
                                                Production Plan Rejected • (
                                                {workOrder?.rejectors?.length}/
                                                {settings?.approversId?.length})
                                            </p>
                                            <i
                                                className={`${
                                                    showApproval === idx
                                                        ? "ri-arrow-down-s-line"
                                                        : "ri-arrow-up-s-line"
                                                }  text-lg text-gm-50 cursor-pointer`}
                                                onClick={() =>
                                                    showApproval !== idx
                                                        ? setShowApproval(idx)
                                                        : setShowApproval(-1)
                                                }
                                            ></i>
                                        </div>

                                        {showApproval === idx && (
                                            <div className="space-y-2 pb-4">
                                                {settings?.approvers?.map((approver, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center justify-between gap-2 pb-2 border-b border-b-[#F1F5F9]"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-[30px] h-[30px] rounded-full bg-slate-100 flex items-center justify-center">
                                                                <i className="ri-user-line text-gm-50"></i>
                                                            </div>
                                                            <div className="">
                                                                <p className="text-g-75 text-[13px]">
                                                                    {approver?.fullName}
                                                                </p>
                                                                <p className="text-slate-500 text-xs  capitalize ">
                                                                    {approver?.role}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <div className="py-1 px-3 bg-slate-100 rounded text-xs text-g-75 ">
                                                                Level {idx + 1}
                                                            </div>
                                                            <div
                                                                className={` ${
                                                                    workOrder?.rejectorIds?.includes(
                                                                        approver?._id
                                                                    )
                                                                        ? "bg-[#FEE2E2] text-slate-900"
                                                                        : "bg-[#F9DFB3] text-g-75"
                                                                } py-1 px-3  rounded text-xs  `}
                                                            >
                                                                {workOrder?.rejectorIds?.includes(
                                                                    approver?._id
                                                                )
                                                                    ? "Rejected"
                                                                    : "Pending"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={rawMaterialModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[1289px]"
                showCloseIcon={false}
                closeModal={() => {
                    setRawMaterialModal(false);
                }}
            >
                <form
                    className="bg-white rounded-[8px] relative font-rocGroteskMedium"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleStartWorkOrder();
                    }}
                >
                    <div className="mt-4 ">
                        <div className="px-6 font-rocGroteskMedium border-b border-b-slate-200 flex items-center justify-between py-3">
                            <p className="mt-1 text-lg text-g-75 ">
                                Select raw material batches to begin work order - {productName}
                            </p>
                            <i
                                className={`cursor-pointer ri-close-fill text-gm-50 `}
                                onClick={() => {
                                    setRawMaterialModal(false);
                                }}
                            ></i>
                        </div>

                        {!tabLoading && notEnoughCount > 0 ? (
                            <div
                                className={`py-3 bg-[#FEF2F2] flex items-center justify-start gap-2 w-full text-[#DC2626] text-sm transition-opacity px-6 `}
                            >
                                <i className="ri-information-fill text-[#DC2626]"></i>
                                Insufficient stock: {notEnoughCount} raw{" "}
                                {notEnoughCount > 1 ? "materials" : "material"} do not have enough
                                inventory to start the work order. Please adjust the quantities or
                                restock before proceeding.
                            </div>
                        ) : (
                            ""
                        )}
                        <div className="bg-slate-50 px-6 py-1.5 ">
                            <p className="text-slate-500 text-sm">
                                Review all raw materials for this production plan to ensure they are
                                in stock. The entered quantities for these raw materials will be
                                locked in inventory. Once you confirm and start the work order,
                                these quantities will be automatically deducted from your inventory.
                            </p>
                        </div>

                        {tabLoading ? (
                            <div className="flex items-center justify-center py-6 h-40">
                                <Loader size={8} />
                            </div>
                        ) : (
                            <div className="px-6 space-y-3 pt-6 mb-9 max-h-[60vh] overflow-y-scroll">
                                <CustomTable
                                    tableBody={BOMTableBody}
                                    tableHeader={BOMTableHeader}
                                    isScrollable={false}
                                    isCellBordered={true}
                                    isCellSelectable={false}
                                    isAllSelectable={false}
                                    scrollWithin={false}
                                    headerContainerClass="!bg-slate-50 !border-slate-200"
                                    headerItemClass="font-rocGroteskMedium !text-[#334155]"
                                    tableClass="!border-slate-200"
                                    isCollapseable={true}
                                    allCellClick={false}
                                    expandedRowIdx={expandedRowIdx}
                                    bodyItemClass="hover:!bg-transparent !cursor-default !border-slate-200"
                                    rowExpanded={
                                        <div className=" bg-[#FFFBF0]">
                                            {stockList?.length === 0 ? (
                                                <div className="overflow-x-scroll">
                                                    <p className="py-2 px-4 text-sm text-slate-900">
                                                        No batch available
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="overflow-x-scroll">
                                                    <CustomTable
                                                        tableBody={expandedTableBody}
                                                        tableHeader={tableHeader}
                                                        scrollWithin={false}
                                                        isCellBordered={false}
                                                        isInifiniteScroll={true}
                                                        tableClass="!bg-[#FFFBF0] !border-t-slate-200 !border-b-0 !border-x-0 !rounded-none mt-[-30px]"
                                                        isScrollable={true}
                                                        tableContainerClass=""
                                                        bodyItemClass="hover:!bg-[#FFFBF0]  !bg-[#FFFBF0]  !cursor-default !border-slate-200"
                                                        expandedRowTClass="!z-[0] !relative"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </div>
                        )}
                        <div
                            className={`border-t border-t-slate-200 py-4 px-6 flex items-center w-full justify-end space-x-4`}
                        >
                            <div className="flex items-center  justify-end gap-4">
                                <Button
                                    btnText={"Cancel"}
                                    btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                    onClick={() => setRawMaterialModal(false)}
                                />
                                <Button
                                    type="submit"
                                    btnText={`Confirm and start work order`}
                                    btnClassname="!py-3 cursor-pointer !text-[14px] !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                    disabled={notEnoughCount > 0}
                                    isLoading={startingWorkOrder}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </ModalContainer>

            {/* <ModalContainer
                open={rawMaterialModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[1089px]"
                showCloseIcon={false}
                closeModal={() => {
                    setRawMaterialModal(false);
                }}
            >
                <form
                    className="bg-white rounded-[8px] relative font-rocGroteskMedium"
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                >
                    <div className="mt-4 ">
                        <div className="px-6 font-rocGroteskMedium border-b border-b-slate-200 flex items-center justify-between py-3">
                            <p className="mt-1 text-lg text-g-75 ">
                                Select raw material batches to begin work order - Kōloa Rum Gold
                            </p>
                            <i
                                className={`cursor-pointer ri-close-fill text-gm-50 `}
                                onClick={() => {
                                    setRawMaterialModal(false);
                                }}
                            ></i>
                        </div>

                        <div className="bg-slate-50 px-6 py-1.5 border-b border-b-slate-200">
                            <p className="text-slate-500 text-sm">
                                Below are all available raw material batches for Kōloa Rum Gold.
                                Please select batch and enter the specific quantities you.d like to
                                allocate for this production run in order to star.
                            </p>
                        </div>

                        <div className="px-6 space-y-3 pt-6">
                            <div className=" ">
                                <p className="text-base text-[#000] mb-4">Quantity on work order</p>
                                <div className="flex flex-grid gap-2  w-auto overflow-x-scroll">
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                    <div className="bg-slate-100 rounded-[6px] w-[211px] p-3">
                                        <p className="text-g-75 text-sm">Sugar Cane</p>
                                        <p className="text-slate-500 text-sm">500 lbs</p>
                                    </div>
                                </div>
                               
                            </div>

                            <div className="!mt-6">
                                <p className="text-base text-[#000]">All raw material batches</p>

                                <div className="flex gap-2 mt-5 mb-4">
                                    <TextInput
                                        name="search"
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e?.target?.value)}
                                        onInput={handleDebouncedSearch}
                                        inputPlaceholder="Search products"
                                        inputClassName={
                                            "!h-[40px] pl-[0px] !mb-0 !font-rocGroteskMedium !text-slate-400"
                                        }
                                        leftIcon={
                                            <i className="ri-search-line before:content-['\f0d1] text-slate-200 mt-[2px]"></i>
                                        }
                                        inputContainerClassname={
                                            "!rounded-[4px]  !h-[40px] !pl-[20px] !bg-white !border !border-slate-200"
                                        }
                                    />

                                    <Dropdown
                                        name="filter"
                                        value={warehouse}
                                        dropdownTriggerClassName={" "}
                                        handleChange={(name, value) => {
                                            setWarehouse(value);
                                        }}
                                        dropdown={
                                            <div className="bg-white flex items-center max-sm:justify-between h-10 gap-2 py-2.5 px-3 shadow-boxShadow-2 border border-slate-200 !rounded">
                                                <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                                    All warehouse locations
                                                </span>

                                                {warehouse && (
                                                    <i
                                                        onClick={handleClearWarehouse}
                                                        className="ri-close-circle-line text-slate-500"
                                                    ></i>
                                                )}
                                                <div className="flex items-center space-x-2">
                                                    <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                                </div>
                                            </div>
                                        }
                                        dropdownClassName={"!w-full !mb-10 !-mt-7"}
                                        dropdownContainerClasses={`shadow-cardShadow-4  !mt-0  !w-[222px] max-sm:!w-full  rounded !max-h-[200px] !p-0 !top-[105%]`}
                                        wholeContainerClass={`max-sm:!w-full`}
                                        dropdownItemsClasses="!bg-transparent border-b-[1px] border-b-slate-100 "
                                        dropdownOptions={[]}
                                    />
                                </div>
                                <CustomTable
                                    tableBody={BatchesTableBody}
                                    tableHeader={BatchesTableHeader}
                                    isScrollable={false}
                                    isCellBordered={true}
                                    isCellSelectable={false}
                                    isAllSelectable={false}
                                    scrollWithin={false}
                                    headerContainerClass="!bg-slate-50 !border-slate-200 "
                                    headerItemClass="font-rocGroteskMedium !text-[#334155]"
                                    tableClass="!border-slate-200"
                                    bodyItemClass="hover:!bg-transparent !border-slate-200"
                                />
                            </div>
                        </div>
                        <div
                            className={`border-t border-t-slate-200 py-4 px-6 flex items-center w-full justify-end space-x-4`}
                        >
                            <div className="flex items-center  justify-end gap-4">
                                <Button
                                    btnText={"Cancel"}
                                    btnClassname="!py-3 cursor-pointer !text-[14px] !bg-n-20 !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                    onClick={() => setRawMaterialModal(false)}
                                />
                                <Button
                                    type="submit"
                                    btnText={`Confirm and start work order`}
                                    btnClassname="!py-3 cursor-pointer !text-[14px] !text-gm-50 !border border-n-20 !font-rocGroteskMedium !px-4 !w-fit"
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </ModalContainer> */}
        </div>
    );
};

export default WorkOrderModal;
